import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { SeoHead } from '@/hooks/usePageSEO';
import { 
  MapPin, Calendar, Trophy, Award, Heart, Phone, Mail, 
  Download, MessageCircle, Star, Shield, ChevronRight,
  ExternalLink, FileText, Dumbbell, Stethoscope, Eye, User, Dog
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppButton, CallButton, FacebookShareButton, QRCodeButton } from '@/components/SocialShare';
import { usePageViews } from '@/hooks/usePageViews';
import ApisAIFooter from '@/components/ApisAIFooter';
import ViewCounter from '@/components/ViewCounter';
import OptimizedImage from '@/components/OptimizedImage';
import { buildImageAlt } from '@/utils/imageSeo';

import { API } from '@/utils/api';
import LeadInquiryForm from '@/components/public-pages/LeadInquiryForm';
const BASE_URL = 'https://apisai.pl';

// Real View Counter Badge
const ViewCounterBadge = ({ pageType, pageSlug }) => {
  const { totalViews, weeklyViews, isLoading } = usePageViews(pageType, pageSlug);
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!show || isLoading || (totalViews === 0 && weeklyViews === 0)) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-4 left-4 z-50 bg-slate-900/95 backdrop-blur-xl border border-emerald-500/30 rounded-2xl px-4 py-3 shadow-lg"
    >
      <div className="flex items-center gap-3 text-sm">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
          <Eye className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">{totalViews}</span>
            <span className="text-gray-400">wyświetleń</span>
          </div>
          {weeklyViews > 0 && (
            <p className="text-emerald-400 text-xs">
              +{weeklyViews} w tym tygodniu
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Helper to get full image URL
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';
import { useTranslation } from 'react-i18next';

// Gold gradient text component
const GoldText = ({ children, className = "" }) => (
  <span className={`bg-clip-text text-transparent bg-gradient-to-r from-[#D4AF37] via-[#F5DEB3] to-[#D4AF37] ${className}`}>
    {children}
  </span>
);

// Badge component
const Badge = ({ icon: Icon, children }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium bg-[#D4AF37]/5 backdrop-blur-sm">
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </div>
);

// Glass card component
const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-[#10182B]/60 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors ${className}`}>
    {children}
  </div>
);

// Stat item component
const StatItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 text-gray-300">
    <Icon className="w-5 h-5 text-[#D4AF37]" />
    <span className="text-gray-400 text-sm">{label}:</span>
    <span className="font-semibold text-white">{value}</span>
  </div>
);

// Pedigree node component
const PedigreeNode = ({ name, titles, gender, children }) => (
  <div className={`min-w-[260px] snap-center p-5 rounded-xl border transition-all duration-300 ${
    gender === 'male' 
      ? 'border-blue-500/30 bg-blue-500/5 hover:border-blue-500/60' 
      : 'border-pink-500/30 bg-pink-500/5 hover:border-pink-500/60'
  }`}>
    <div className="flex items-center gap-2 mb-2">
      <span className={`text-lg ${gender === 'male' ? 'text-blue-400' : 'text-pink-400'}`}>
        {gender === 'male' ? '♂' : '♀'}
      </span>
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
        {gender === 'male' ? 'Ojciec' : 'Matka'}
      </span>
    </div>
    <h4 className="text-white font-semibold text-lg mb-1">{name || '—'}</h4>
    {titles && <p className="text-gray-400 text-sm">{titles}</p>}
    {children}
  </div>
);

export default function PublicStud() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { totalViews, lastView } = usePageViews('stud', slug);
  const [stud, setStud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [similarStuds, setSimilarStuds] = useState(null);

  useEffect(() => {
    const fetchStud = async () => {
      try {
        const res = await axios.get(`${API}/public/stud/${slug}`);
        setStud(res.data);
        // Fetch similar studs in parallel
        axios.get(`${API}/public/stud/${slug}/similar`).then(r => setSimilarStuds(r.data)).catch(() => {});
      } catch (e) {
        setError('Nie znaleziono reproduktora');
      } finally {
        setLoading(false);
      }
    };
    fetchStud();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !stud) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Nie znaleziono</h1>
          <Link to="/" className="text-[#D4AF37] hover:underline">Wróć do strony głównej</Link>
        </div>
      </div>
    );
  }

  // Calculate age
  const birthYear = stud.birth_date ? new Date(stud.birth_date).getFullYear() : null;
  const age = birthYear ? new Date().getFullYear() - birthYear : null;

  // Get main image - check multiple sources
  const heroImage = getImageUrl(stud.main_photo) || getImageUrl(stud.cover_image) || (stud.gallery && stud.gallery.length > 0 ? getImageUrl(stud.gallery[0]) : null) || PLACEHOLDER_IMAGE;
  const hasGallery = stud.gallery && stud.gallery.length > 0;

  // Typography + footer theme (premium redesign)
  const fontTypo = stud.font_style === 'modern' ? 'pub-typo-modern' : 'pub-typo-elegant';
  const footerTheme = { bg: 'bg-[#0B0F1A]', border: 'border-white/10', text: 'text-white', textMuted: 'text-white/60' };

  // Generate SEO data
  const seoTitle = `${stud.name} - ${stud.breed} Reproduktor | ApisAI`;
  const _studDescBuilt = `${stud.name} - profesjonalny reproduktor rasy ${stud.breed}${stud.city ? ` z ${stud.city}` : ''}. Sprawdź rodowód, osiągnięcia i badania zdrowotne.`;
  const _studDescRaw = (stud.description || '').trim();
  const seoDescription = _studDescRaw.length >= 50 ? _studDescRaw.slice(0, 160) : _studDescBuilt;
  const seoImage = stud.main_photo ? getImageUrl(stud.main_photo) : null;
  const heroAlt = buildImageAlt({ kind: 'stud', name: stud.name, breed: stud.breed, city: stud.city });
  const imageSchema = seoImage ? {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": seoImage,
    "url": seoImage,
    "caption": heroAlt,
    "representativeOfPage": true,
  } : null;
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": stud.name,
    "description": seoDescription,
    "brand": { "@type": "Brand", "name": stud.breed },
    "category": "Reproduktor",
    "image": seoImage,
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "PLN",
      "price": stud.stud_fee || "0",
      "seller": {
        "@type": "Organization",
        "name": stud.kennel_name || "Hodowla",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": stud.city,
          "addressCountry": "PL"
        }
      }
    }
  };

  const profilePageSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": stud.name,
      "description": seoDescription,
      "image": seoImage,
      "memberOf": stud.kennel?.name ? {
        "@type": "Organization",
        "name": stud.kennel.name,
        "url": stud.kennel?.slug ? `${BASE_URL}/hodowla/${stud.kennel.slug}` : undefined
      } : undefined
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Strona glowna", "item": BASE_URL },
        { "@type": "ListItem", "position": 2, "name": "Reproduktory", "item": `${BASE_URL}/reproduktory` },
        { "@type": "ListItem", "position": 3, "name": stud.breed, "item": `${BASE_URL}/reproduktory/${stud.breed?.toLowerCase().replace(/\\s+/g, '-')}` },
        { "@type": "ListItem", "position": 4, "name": stud.name, "item": `${BASE_URL}/reproduktor/${slug}` }
      ]
    }
  };

  return (
    <div className={`min-h-screen bg-[#0B0F1A] relative overflow-hidden pub-body ${fontTypo}`}>
      {/* SEO Meta Tags + AI Optimization (bezpośrednie wstrzykiwanie do <head>) */}
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonical={`${BASE_URL}/reproduktor/${slug}`}
        ogType="product"
        ogImage={seoImage || `${BASE_URL}/logo-apisai.png`}
        ogImageWidth="1200"
        ogImageHeight="630"
        ogImageAlt={heroAlt}
        ogSiteName="ApisAI - Profesjonalne strony hodowli"
        robots="index, follow, max-image-preview:large"
        geoPlacename={stud.city}
        schemas={[
          schemaData,
          profilePageSchema,
          imageSchema,
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Jakie są warunki krycia z ${stud.name}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": stud.stud_fee ? `Opłata za krycie: ${stud.stud_fee} PLN. Skontaktuj się z właścicielem aby ustalić szczegóły.` : `Skontaktuj się z właścicielem aby ustalić warunki krycia.`
                }
              },
              {
                "@type": "Question",
                "name": `Czy ${stud.name} ma badania zdrowotne?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": stud.health_tests?.length > 0 ? `Tak, ${stud.name} posiada badania: ${stud.health_tests.join(', ')}.` : `Skontaktuj się z właścicielem aby uzyskać informacje o badaniach zdrowotnych.`
                }
              },
              {
                "@type": "Question",
                "name": `Gdzie znajduje się reproduktor ${stud.name}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `${stud.name} znajduje się w ${stud.city}${stud.voivodeship ? `, woj. ${stud.voivodeship}` : ''}, Polska.`
                }
              }
            ]
          }
        ].filter(Boolean)}
      />

      {/* Noise overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt={heroAlt} className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* ============ MODERN LAYOUT - Two Column ============ */}
      <section className="min-h-screen bg-[#0B0F1A] pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
        
        {/* Header with breed and name */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <p className="text-[#D4AF37] font-medium tracking-[0.3em] uppercase text-sm" style={{ fontFamily: 'Cinzel, serif' }}>
              {stud.breed}
            </p>
            {stud.kennel_club && (
              <span className="px-3 py-1 bg-white/10 text-white rounded-full text-xs font-semibold border border-white/20">
                {stud.kennel_club}
              </span>
            )}
            {stud.is_fci && (
              <span data-testid="stud-badge-fci" className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-bold tracking-wider uppercase border border-blue-400/30">
                FCI
              </span>
            )}
            {stud.organization_type && stud.organization_type !== 'unknown' && stud.organization_type !== '' && !stud.kennel_club && (
              <span data-testid="stud-badge-org" className="px-3 py-1 bg-white/10 text-white/80 rounded-full text-xs font-semibold border border-white/20">
                {stud.organization_type === 'zkwp_fci' ? 'ZKwP' : stud.organization_type === 'fci_other' ? 'FCI' : stud.organization_type === 'khron' ? 'KHRON' : stud.organization_type === 'wush' ? 'WUSH' : stud.organization_type === 'other' ? 'Inna org.' : ''}
              </span>
            )}
            {stud.stud_available && (
              <span data-testid="stud-badge-available" className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-semibold border border-emerald-500/30">
                Dostępny do krycia
              </span>
            )}
          </div>
          {/* Name - always show */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-none" style={{ fontFamily: 'var(--pub-head)' }}>
            <GoldText>{stud.name}</GoldText>
          </h1>
          {/* Pedigree name - same style as main name */}
          {stud.pedigree_name && stud.pedigree_name !== stud.name && (
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-none mt-1" style={{ fontFamily: 'var(--pub-head)' }}>
              <GoldText>{stud.pedigree_name}</GoldText>
            </h2>
          )}

          {/* OLX-style view counter */}
          <div className="mt-4">
            <ViewCounter totalViews={totalViews} lastView={lastView} slug={slug} variant="detail" />
          </div>
          
          {/* Badges row - unified styling */}
          <div className="flex flex-wrap gap-2 mt-4">
            {stud.selected_championships?.map((ch, i) => {
              // Map championship IDs to readable labels
              const champLabels = {
                ch_pl: 'Ch.PL',
                ch_int: 'C.I.B.',
                ch_club: 'Ch.Klub',
                ch_youth: 'Mł.Ch.PL',
                ch_world: 'Ch.Świata',
                ch_winner: 'Zwycięzca PL'
              };
              const label = champLabels[ch] || ch.replace('ch_', 'Ch.').replace('_', ' ');
              return (
                <span key={i} className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full text-sm font-medium border border-[#D4AF37]/30">
                  {label}
                </span>
              );
            })}
            {(stud.selected_training || stud.training || []).map((tr, i) => (
              <span key={`tr-${i}`} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium border border-blue-500/30">
                {tr}
              </span>
            ))}
            {stud.health_tests_text?.split(',').map((test, i) => (
              <span key={`ht-${i}`} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium border border-emerald-500/30">
                {test.trim()}
              </span>
            ))}
          </div>
        </div>

        {/* Two column layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* LEFT COLUMN - Main Photo */}
          <div className="space-y-6">
            {/* Main Photo */}
            <div 
              className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer group"
              onClick={() => heroImage !== PLACEHOLDER_IMAGE && setLightboxImage(heroImage)}
            >
              <OptimizedImage 
                src={getImageUrl(stud.main_photo)} 
                alt={heroAlt}
                priority={true}
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Location & Age info */}
            <div className="flex flex-wrap gap-4 text-gray-300">
              {stud.city && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span>{stud.city}{stud.voivodeship ? `, ${stud.voivodeship}` : ''}</span>
                </div>
              )}
              {age && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  <span>{age} {age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - Oferta Krycia Box */}
          <div className="bg-[#10182B]/80 backdrop-blur-md border border-[#D4AF37]/20 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3" style={{ fontFamily: 'var(--pub-head)' }}>
              <Heart className="w-8 h-8 text-[#D4AF37]" />
              Oferta krycia
            </h2>

            {/* 1. Opis psa */}
            {stud.description && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Opis</h3>
                <p className="text-gray-300 leading-relaxed">{stud.description}</p>
              </div>
            )}

            {/* 2. Rodowód - tylko numer i PDF, nazwa jest już w nagłówku */}
            {(stud.pedigree_number || stud.pedigree_file || stud.pedigree_link) && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Rodowód</h3>
                {stud.pedigree_number && (
                  <p className="text-white font-medium">Nr: {stud.pedigree_number}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {stud.pedigree_file && (
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      onClick={() => window.open(getImageUrl(stud.pedigree_file), '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Pobierz PDF
                    </Button>
                  )}
                  {stud.pedigree_link && (
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      onClick={() => window.open(stud.pedigree_link, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Baza online
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* 3. Warunki krycia */}
            {stud.stud_conditions && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Warunki krycia</h3>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">{stud.stud_conditions}</p>
              </div>
            )}

            {/* 4. Rodzice */}
            {(stud.sire_name || stud.dam_name) && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">Rodzice</h3>
                <div className="space-y-3">
                  {stud.sire_name && (
                    <div className="flex items-start gap-3">
                      <span className="text-blue-400 text-xl">♂</span>
                      <div>
                        <p className="text-white font-medium">{stud.sire_name}</p>
                        {stud.sire_titles && <p className="text-[#D4AF37] text-sm">{stud.sire_titles}</p>}
                      </div>
                    </div>
                  )}
                  {stud.dam_name && (
                    <div className="flex items-start gap-3">
                      <span className="text-pink-400 text-xl">♀</span>
                      <div>
                        <p className="text-white font-medium">{stud.dam_name}</p>
                        {stud.dam_titles && <p className="text-[#D4AF37] text-sm">{stud.dam_titles}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 5. Co zawiera cena */}
            {stud.stud_fee_includes && stud.stud_fee_includes.length > 0 && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">Co zawiera cena</h3>
                <ul className="space-y-2">
                  {stud.stud_fee_includes.map((item, idx) => {
                    // Map ID to label
                    const labels = {
                      contract: 'Umowa krycia',
                      repeat: 'Warunki powtórki (jeśli suka nie zajdzie)',
                      support: 'Wsparcie i konsultacje',
                      pedigree_copy: 'Kopia rodowodu reproduktora',
                      health_results: 'Wyniki badań zdrowotnych',
                      photos: 'Zdjęcia z krycia',
                      video: 'Film z krycia',
                      transport: 'Transport/dojazd',
                      accommodation: 'Zakwaterowanie suczki',
                      ai: 'Inseminacja (AI)'
                    };
                    const label = labels[item] || item;
                    return (
                      <li key={idx} className="flex items-center gap-2 text-gray-300">
                        <Shield className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                        <span className="text-sm">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* 6. Cena */}
            {stud.stud_price && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Cena krycia</h3>
                <p className="text-3xl font-bold">
                  <GoldText>{stud.stud_price}</GoldText>
                </p>
              </div>
            )}

            {/* 6. Szybki kontakt */}
            <div>
              <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Szybki kontakt</h3>
              <div className="grid grid-cols-2 gap-3">
                {(stud.contact_phone || stud.contact_whatsapp) && (
                  <>
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => window.open(`https://wa.me/${(stud.contact_whatsapp || stud.contact_phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Dzień dobry! Interesuję mnie reproduktor ${stud.name}`)}`, '_blank')}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => window.location.href = `tel:${stud.contact_phone || stud.contact_whatsapp}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Telefon
                    </Button>
                  </>
                )}
                {stud.contact_email && (
                  <Button 
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-white/10"
                    onClick={() => window.location.href = `mailto:${stud.contact_email}?subject=Zapytanie o ${stud.name}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
                {stud.contact_facebook && (
                  <Button 
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => window.open(stud.contact_facebook, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Facebook
                  </Button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

      {/* ============ GALLERY ============ */}
      {hasGallery && (
        <section className="py-16 bg-[#10182B]/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" style={{ fontFamily: 'var(--pub-head)' }}>
                Galeria
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stud.gallery.map((img, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className={`relative group cursor-pointer overflow-hidden rounded-xl ${
                      i === 0 ? 'col-span-2 row-span-2' : ''
                    }`}
                    onClick={() => setLightboxImage(getImageUrl(img))}
                  >
                    <img 
                      src={getImageUrl(img)}
                      alt={buildImageAlt({ kind: 'stud', name: stud.name, breed: stud.breed, city: stud.city, index: i })}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover aspect-square group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ RELATED LITTERS ============ */}
      {stud.related_litters?.length > 0 && (
        <section className="py-16 bg-[#0B0F1A]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'var(--pub-head)' }}>
                Powiązane mioty
              </h2>
              <p className="text-gray-400 mb-8">Mioty, w których {stud.name} jest ojcem</p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stud.related_litters.map((litter) => (
                  <Link 
                    key={litter.id} 
                    to={`/miot/${litter.slug}`}
                    className="block bg-[#10182B]/60 backdrop-blur-md border border-white/5 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    data-testid={`related-litter-${litter.slug}`}
                  >
                    {litter.cover_image && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={getImageUrl(litter.cover_image)} 
                          alt={litter.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-white font-semibold text-lg mb-1">{litter.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{litter.breed}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {litter.birth_date && (
                          <span className="px-2 py-1 bg-white/5 text-gray-300 rounded-full flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(litter.birth_date).toLocaleDateString('pl-PL')}
                          </span>
                        )}
                        {litter.status && (
                          <span className={`px-2 py-1 rounded-full ${
                            litter.status === 'available' ? 'bg-emerald-500/20 text-emerald-400' : 
                            litter.status === 'reserved' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {litter.status === 'available' ? 'Dostępne' : litter.status === 'reserved' ? 'Zarezerwowane' : litter.status === 'sold' ? 'Sprzedane' : litter.status}
                          </span>
                        )}
                        {litter.kennel_name && (
                          <span className="px-2 py-1 bg-white/5 text-gray-300 rounded-full">
                            {litter.kennel_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ KENNEL INFO ============ */}
      {stud.kennel && (
        <section className="py-12 bg-[#10182B]/30">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col md:flex-row items-center gap-6 p-6 bg-[#10182B]/60 backdrop-blur-md border border-white/5 rounded-2xl">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0">
                  <User className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="text-gray-400 text-sm mb-1">Hodowla</p>
                  <h3 className="text-xl font-bold text-white">{stud.kennel.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
                    {stud.kennel.city && (
                      <span className="text-gray-400 text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {stud.kennel.city}{stud.kennel.voivodeship ? `, ${stud.kennel.voivodeship}` : ''}
                      </span>
                    )}
                    {stud.kennel.is_fci && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs font-bold rounded-full border border-blue-400/30">FCI</span>
                    )}
                    {stud.kennel.organization_type && stud.kennel.organization_type !== 'unknown' && (
                      <span className="px-2 py-0.5 bg-white/10 text-white/70 text-xs font-medium rounded-full border border-white/20">
                        {stud.kennel.organization_type === 'zkwp_fci' ? 'ZKwP' : stud.kennel.organization_type === 'fci_other' ? 'FCI' : stud.kennel.organization_type === 'khron' ? 'KHRON' : stud.kennel.organization_type === 'wush' ? 'WUSH' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <Link to={`/hodowla/${stud.kennel.slug}`}>
                  <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold">
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Zobacz hodowlę
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============ CONTACT SECTION - Full Width ============ */}
      <section id="contact-section" className="py-16 bg-[#10182B]/50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'var(--pub-head)' }}>
              Skontaktuj się z nami
            </h2>
            <p className="text-gray-400 mb-10 text-center">Masz pytania o {stud.name}? Chętnie odpowiemy!</p>

            <div className="grid lg:grid-cols-2 gap-8">
              
              {/* LEFT - Contact Info & Quick Actions */}
              <div className="space-y-6">
                {/* Owner/Kennel info */}
                {(stud.kennel?.name || stud.owner?.name) && (
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg">{stud.kennel?.name || stud.owner?.name}</p>
                      {stud.city && (
                        <p className="text-gray-400 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {stud.city}{stud.voivodeship ? `, ${stud.voivodeship}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Buttons Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* WhatsApp */}
                  {(stud.contact_whatsapp || stud.contact_phone) && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white h-14 text-base"
                      onClick={() => window.open(`https://wa.me/${(stud.contact_whatsapp || stud.contact_phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Dzień dobry! Interesuję mnie reproduktor ${stud.name}`)}`, '_blank')}
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  )}

                  {/* Phone */}
                  {stud.contact_phone && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white h-14 text-base"
                      onClick={() => window.location.href = `tel:${stud.contact_phone}`}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Zadzwoń
                    </Button>
                  )}

                  {/* Facebook */}
                  {stud.contact_facebook && (
                    <Button 
                      variant="outline"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-14 text-base"
                      onClick={() => window.open(stud.contact_facebook, '_blank')}
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Facebook
                    </Button>
                  )}

                  {/* Instagram */}
                  {stud.contact_instagram && (
                    <Button 
                      variant="outline"
                      className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10 h-14 text-base"
                      onClick={() => window.open(stud.contact_instagram, '_blank')}
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Instagram
                    </Button>
                  )}
                </div>

                {/* Location */}
                {stud.city && (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white font-medium">Lokalizacja</span>
                    </div>
                    <p className="text-gray-300">{stud.city}{stud.voivodeship ? `, woj. ${stud.voivodeship}` : ''}</p>
                  </div>
                )}

                {/* Share Profile */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white font-medium">Udostępnij profil</span>
                  </div>
                  <div className="flex gap-3">
                    <QRCodeButton url={`${BASE_URL}/reproduktor/${slug}`} title={stud.name} />
                    <FacebookShareButton url={`${BASE_URL}/reproduktor/${slug}`} title={`${stud.name} - ${stud.breed}`} />
                  </div>
                </div>
              </div>

              {/* RIGHT - Contact Form */}
              <div className="bg-white/5 rounded-2xl p-6 md:p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Wyślij wiadomość</h3>

                {stud.is_claimed === false ? (
                  <div data-testid="stud-lead-blocked" className="text-sm text-white/70 leading-relaxed">
                    <p className="mb-3">
                      Ten profil reproduktora to <strong className="text-white">nieoficjalna wizytówka</strong> utworzona na podstawie publicznie dostępnych danych. Formularz kontaktowy jest nieaktywny do czasu przejęcia profilu przez właściciela.
                    </p>
                    <p>Jesteś właścicielem? Skorzystaj z opcji „Przejmij profil".</p>
                  </div>
                ) : (
                  <LeadInquiryForm
                    entityType="stud"
                    entityId={stud.id || stud.slug || slug}
                    entityName={stud.name}
                    accent="gold"
                    defaultMessage={`Dzień dobry, interesuje mnie reproduktor ${stud.name}...`}
                    submitLabel="Wyślij wiadomość"
                    mailtoEmail={stud.contact_email}
                    testIdPrefix="stud-lead"
                  />
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Similar Studs Section */}
      {similarStuds && (similarStuds.same_breed?.length > 0 || similarStuds.same_area?.length > 0 || similarStuds.same_kennel?.length > 0) && (
        <section className="py-16 px-6" data-testid="similar-studs-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Podobne reproduktory</h2>
            
            {similarStuds.same_breed?.length > 0 && (
              <div className="mb-10">
                <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  Reproduktory tej samej rasy
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {similarStuds.same_breed.map((s, i) => (
                    <Link key={s.slug || i} to={`/reproduktor/${s.slug}`}
                      className="bg-[#10182B]/60 border border-white/5 rounded-xl p-4 hover:border-amber-500/30 transition" data-testid="similar-stud-card">
                      <div className="h-32 bg-[#0B0F1A] rounded-lg overflow-hidden mb-3">
                        {s.main_photo ? (
                          <img src={getImageUrl(s.main_photo)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Dog className="w-8 h-8 text-gray-700" /></div>
                        )}
                      </div>
                      <h4 className="text-white font-medium text-sm line-clamp-1">{s.name}</h4>
                      <p className="text-gray-400 text-xs mt-1">{s.breed}</p>
                      {s.city && <p className="text-gray-500 text-xs flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{s.city}</p>}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {similarStuds.same_area?.length > 0 && (
              <div className="mb-10">
                <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Reproduktory w Twojej okolicy
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {similarStuds.same_area.map((s, i) => (
                    <Link key={s.slug || i} to={`/reproduktor/${s.slug}`}
                      className="bg-[#10182B]/60 border border-white/5 rounded-xl p-4 hover:border-emerald-500/30 transition" data-testid="similar-area-card">
                      <div className="h-32 bg-[#0B0F1A] rounded-lg overflow-hidden mb-3">
                        {s.main_photo ? (
                          <img src={getImageUrl(s.main_photo)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Dog className="w-8 h-8 text-gray-700" /></div>
                        )}
                      </div>
                      <h4 className="text-white font-medium text-sm line-clamp-1">{s.name}</h4>
                      <p className="text-gray-400 text-xs mt-1">{s.breed}</p>
                      {s.city && <p className="text-gray-500 text-xs flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{s.city}</p>}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {similarStuds.same_kennel?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-orange-400 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Inne psy tej hodowli
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {similarStuds.same_kennel.map((s, i) => (
                    <Link key={s.slug || i} to={`/reproduktor/${s.slug}`}
                      className="bg-[#10182B]/60 border border-white/5 rounded-xl p-4 hover:border-orange-500/30 transition" data-testid="similar-kennel-card">
                      <div className="h-32 bg-[#0B0F1A] rounded-lg overflow-hidden mb-3">
                        {s.main_photo ? (
                          <img src={getImageUrl(s.main_photo)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Dog className="w-8 h-8 text-gray-700" /></div>
                        )}
                      </div>
                      <h4 className="text-white font-medium text-sm line-clamp-1">{s.name}</h4>
                      <p className="text-gray-400 text-xs mt-1">{s.breed}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Internal SEO Links */}
      <section className="pb-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-4 text-sm">
          {stud.breed && (
            <Link to={`/reproduktory/${stud.breed.toLowerCase().replace(/\s+/g, '-')}`} 
              className="text-gray-400 hover:text-amber-300 transition flex items-center gap-1">
              <Heart className="w-3.5 h-3.5" /> Reproduktory {stud.breed}
            </Link>
          )}
          {stud.breed && (
            <Link to={`/rasy/${stud.breed.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-gray-400 hover:text-white transition flex items-center gap-1">
              <Dog className="w-3.5 h-3.5" /> O rasie {stud.breed}
            </Link>
          )}
          <Link to="/reproduktory" className="text-gray-400 hover:text-white transition flex items-center gap-1">
            <Heart className="w-3.5 h-3.5" /> Wszystkie reproduktory
          </Link>
        </div>
      </section>

      {/* ApisAI System Footer */}
      <ApisAIFooter pageType="stud" pageSlug={slug} theme={footerTheme} />

      {/* Real View Counter */}
      <ViewCounterBadge pageType="stud" pageSlug={slug} />
    </div>
  );
}
